// ---------------------------------------------------------------------------
// District-heating network engineering — pure functions shared by the map island
// and (potentially) the server. Reuses the AustroPUR selection tables in
// pipeData.ts: given a segment's power (kW) and effective ΔT, we read off the
// suitable pipe diameters and their specific pressure drop (Pa/m), then match a
// real AustroPUR product by medium-pipe diameter.
//
// Hydraulic conventions match the other apps: water, cp = 1.163 Wh/(kg·K), so
// volumetric flow (m³/h) = kW / (1.163 · ΔT). Heat loss uses the datasheet
// U-values (W/m·K) from pipeData's TEMP_LOSS tables.
// ---------------------------------------------------------------------------

import {
  DELTA_T_COLUMNS,
  PIPE_COLUMNS,
  DT_KW,
  KW_PIPE,
  TEMP_LOSS_SINGLE,
  TEMP_LOSS_DUO,
} from "./pipeData.js";

/** Specific heat of water, Wh/(kg·K) — kW/(1.163·ΔT) yields m³/h directly. */
export const CP_WH = 1.163;
/** Specific heat of water, J/(kg·K) — for temperature-drop from heat loss. */
export const CP_J = 4187;
/** Water density (kg/m³) at distribution temperatures (~0.97 of cold). */
export const RHO = 971;

/** Volumetric flow (m³/h) to carry `kW` at spread `deltaT` (K). */
export function flowM3h(kW: number, deltaT: number): number {
  return deltaT > 0 ? kW / (CP_WH * deltaT) : 0;
}

/** Mass flow (kg/s) to carry `kW` at spread `deltaT` (K). */
export function massFlowKgS(kW: number, deltaT: number): number {
  return (flowM3h(kW, deltaT) * RHO) / 3600;
}

/**
 * Flow-weighted effective ΔT of a trunk carrying several loads. Each load pulls
 * its own ṁ = Q/(cp·ΔTᵢ); the mixed spread is ΣQ / Σ(Q/ΔTᵢ).
 */
export function effectiveDeltaT(loads: { kW: number; deltaT: number }[]): number {
  let q = 0;
  let flowSum = 0;
  for (const l of loads) {
    if (l.kW <= 0 || l.deltaT <= 0) continue;
    q += l.kW;
    flowSum += l.kW / l.deltaT;
  }
  return flowSum > 0 ? q / flowSum : 0;
}

export type PipePick = { diameter: number; dropPerM: number };

/** Nearest index into a numeric column set. */
function nearestIndex(cols: number[], value: number): number {
  let idx = 0;
  let best = Infinity;
  for (let i = 0; i < cols.length; i++) {
    const d = Math.abs(cols[i]! - value);
    if (d < best) {
      best = d;
      idx = i;
    }
  }
  return idx;
}

/**
 * Suitable AustroPUR diameters + specific pressure drop (Pa/m) for a required
 * power at a given ΔT. Snaps ΔT to the nearest table column, finds the mass-flow
 * row whose kW is closest to the requirement, and reads that row of KW_PIPE.
 * Returned ascending by diameter (so the first is the smallest/cheapest pipe).
 */
export function pickDiameters(powerKw: number, deltaT: number): PipePick[] {
  if (powerKw <= 0 || deltaT <= 0) return [];
  const col = nearestIndex(DELTA_T_COLUMNS, deltaT);
  let row = 0;
  let best = Infinity;
  for (let i = 0; i < DT_KW.length; i++) {
    const v = DT_KW[i]?.[col];
    if (v == null) continue;
    const d = Math.abs(v - powerKw);
    if (d < best) {
      best = d;
      row = i;
    }
  }
  const kwRow = KW_PIPE[Math.min(row, KW_PIPE.length - 1)] ?? [];
  const picks: PipePick[] = [];
  for (let i = 0; i < kwRow.length; i++) {
    const drop = kwRow[i];
    if (drop != null) picks.push({ diameter: PIPE_COLUMNS[i]!, dropPerM: drop });
  }
  return picks;
}

/**
 * Core-bore (Kernbohrung) threshold for a Dichtungskette, in mm. Bores are cored
 * in coarse steps, so the requirement is snapped to the nearest 50 mm — and it
 * can never land at or below the pipe it has to clear.
 */
export function boreThresholdMm(pipeMm: number): number {
  let bore = Math.round(pipeMm / 50) * 50;
  if (bore <= pipeMm) bore += 50;
  return bore;
}

/** Insulation U-value (W/m·K) for a pipe type + jacket class + medium Ø. */
export function uWertFor(type: string, jacket: string, diameter: number): number | null {
  const table = type === "Duo" ? TEMP_LOSS_DUO : TEMP_LOSS_SINGLE;
  const dim = String(diameter);
  const exact = table.find((r) => r.insulation === jacket && r.dimension === dim);
  if (exact) return exact.uWert;
  // Fall back to any row for this diameter (nearest jacket) so a slightly
  // different jacket class still yields a sane heat-loss estimate.
  const byDim = table.filter((r) => r.dimension === dim);
  if (byDim.length) return byDim.reduce((a, b) => (b.uWert < a.uWert ? b : a)).uWert;
  return null;
}

/**
 * Temperature drop (K) along a segment from insulation heat loss.
 * loss(W) = U·L·(fluid − ground); ΔT = loss / (ṁ·cp).
 */
export function tempDropK(
  uWert: number,
  lengthM: number,
  fluidTemp: number,
  groundTemp: number,
  massKgS: number,
): number {
  if (massKgS <= 0) return 0;
  const lossW = Math.max(0, uWert * lengthM * (fluidTemp - groundTemp));
  return lossW / (massKgS * CP_J);
}

export type PipeType = "Duo" | "Single" | "Auto";

export type ProductLike = { diameter: number; type: string };

/**
 * Choose a diameter for a segment: the smallest suitable AustroPUR pipe whose
 * pressure gradient is within `maxPaPerM` and that exists as a product of the
 * requested type. Falls back to the lowest-gradient suitable pipe (flagged) when
 * none meets the target, and reports when nothing in the tables fits at all.
 */
export function chooseSegmentPipe(opts: {
  powerKw: number;
  deltaT: number;
  maxPaPerM: number;
  pipeType: PipeType;
  products: ProductLike[];
}): { diameter: number; dropPerM: number; type: "Duo" | "Single"; exceedsGradient: boolean } | null {
  const { powerKw, deltaT, maxPaPerM, pipeType, products } = opts;
  const picks = pickDiameters(powerKw, deltaT);
  if (!picks.length) return null;

  const typeOrder: ("Duo" | "Single")[] = pipeType === "Single" ? ["Single"] : pipeType === "Duo" ? ["Duo"] : ["Duo", "Single"];
  const hasProduct = (d: number, t: "Duo" | "Single") => products.some((p) => Math.round(p.diameter) === d && p.type === t);

  // Prefer the smallest diameter within the gradient target that also exists as
  // a product of an allowed type.
  for (const t of typeOrder) {
    for (const pick of picks) {
      if (pick.dropPerM <= maxPaPerM && hasProduct(pick.diameter, t)) {
        return { diameter: pick.diameter, dropPerM: pick.dropPerM, type: t, exceedsGradient: false };
      }
    }
  }
  // None within target: take the lowest-gradient pipe that is buyable (largest Ø).
  for (const t of typeOrder) {
    const buyable = picks.filter((p) => hasProduct(p.diameter, t));
    if (buyable.length) {
      const best = buyable.reduce((a, b) => (b.dropPerM < a.dropPerM ? b : a));
      return { diameter: best.diameter, dropPerM: best.dropPerM, type: t, exceedsGradient: best.dropPerM > maxPaPerM };
    }
  }
  // No product of any allowed type — still report the smallest table diameter so
  // the caller can flag "no matching AustroPUR pipe".
  const smallest = picks[0]!;
  return { diameter: smallest.diameter, dropPerM: smallest.dropPerM, type: typeOrder[0]!, exceedsGradient: smallest.dropPerM > maxPaPerM };
}
